import { Theme } from '@material-ui/core/styles';

/**
 * Fountain Design System tokens for the Offer Letter Templates feature.
 *
 * Semantic names for the official "Offer Letter Customization (Tabbed Rail)"
 * design handoff, sourced from `theme.palette.common` — the same
 * `@fountain/fountain-ui-components` theme every other recruiter-ui container
 * consumes — rather than a hand-copied hex table. `theme.palette.common.*` is
 * itself generated from `packages/fds-tokens/colors.ts`
 * (`syncFdsColors`), so this can't drift from the source of truth the way a
 * local copy could (review, PR #647).
 *
 * Radii, shadows, and the purple/indigo conditional-block accent have no
 * generated FDS source (fds-tokens is colors-only, and purple isn't a real
 * FDS color — `packages/fds-tokens/colors.ts` has no purple entries at all)
 * and stay as local constants. If the design system adds a real purple token
 * later, replace these with `theme.palette.common.*` like everything else
 * here.
 */
export const getFDS = (theme: Theme) => ({
  // Brand blue
  blue50: theme.palette.common.blue50,
  blue100: theme.palette.common.blue100,
  blue500: theme.palette.common.blue500,
  blue600: theme.palette.common.blue600,

  // Neutral
  gray100: theme.palette.common.gray100, // page bg / chip bg / search field
  gray200: theme.palette.common.gray200, // borders, dividers, row separators
  gray400: theme.palette.common.gray400, // placeholder / empty-select text
  gray500: theme.palette.common.gray500, // hint text / timestamps
  gray600: theme.palette.common.gray600, // secondary text
  gray800: theme.palette.common.gray800, // headings / primary text

  // Surfaces
  surfaceMuted: theme.palette.common.gray100,
  surfaceSunken: 'var(--ds-color-neutral-50, #f8fafc)',
  // Expanded rail row. A second custom tint, one step lighter and cooler than
  // surfaceSunken, because the handoff gives hover and expanded DIFFERENT
  // backgrounds (#f8fafc / #fafbff) and both rails had collapsed them onto the
  // same value — so an open row read as merely hovered (AXHE-4225 §2.1). Named
  // here rather than inlined: the ux-design guard prohibits raw hex in
  // components, and surfaceSunken already sets the precedent for a custom tint
  // that has no generated FDS source.
  surfaceRaised: '#fafbff',

  // Warning (unmapped tokens, ⚠ icon)
  orange100: theme.palette.common.orange100,
  orange400: theme.palette.common.orange400,
  // The border on a warning tint, separate from the tint itself: a border and
  // the background it encloses do not take the same weight.
  borderWarning: theme.palette.common.warningBorder,
  // Icon-only warning accent. orange600 clears the 3:1 non-text minimum on
  // white (3.11:1) but NOT the 4.5:1 text minimum — never use it for text.
  fgWarning: theme.palette.common.orange600,
  // Warning text sitting on an orange tint (unmapped variable tokens,
  // unconfigured conditional markers). The design handoff paired orange600 /
  // orange400 with orange100, which measured 2.82:1 and 2.54:1 in the sandbox —
  // both well under WCAG AA's 4.5:1. orange800 on orange100 is 11.6:1
  // (AXHE-4064 §D).
  fgWarningOnTint: theme.palette.common.orange800,

  // Success — the active signer's signature placeholder is actually in the
  // document (AXHE-4391). Split into two tokens for the same reason the warning
  // pair above is, and the split is measured, not stylistic: on white, green800
  // is 11.6:1 and carries the hint text; green600 is 3.49:1, which clears the
  // 3:1 non-text minimum for the ✓ but NOT AA's 4.5:1 for text. They are not
  // interchangeable. green800 is also what FDS's own `InlineMessage` uses for
  // success text, so this is the house treatment rather than a local choice.
  fgSuccess: theme.palette.common.green800,
  fgSuccessIcon: theme.palette.common.green600,

  // Error — a condition row the Save guard refuses (AXHE-4366 §3). Distinct
  // from the warning tokens above on purpose: orange means "not finished yet",
  // and the rails already spend it on that. These three mean "this is what
  // stopped the save", which is a different message and needs to be
  // distinguishable from the ⚠ it sits next to.
  //
  // Measured, since both foregrounds carry text: red600 is 6.1:1 on white and
  // 4.9:1 on the red100 tint, so one token is legible in both places. red400 is
  // the border only — 4.5:1 on white, past the 3:1 non-text minimum.
  fgError: theme.palette.common.red600,
  surfaceError: theme.palette.common.red100,
  borderError: theme.palette.common.red400,

  // Semantic aliases
  fgPrimary: theme.palette.common.gray800,
  fgSecondary: theme.palette.common.gray600,
  fgTertiary: theme.palette.common.gray500,
  borderSubtle: theme.palette.common.gray200,
  borderDefault: theme.palette.common.gray300,
  brand: theme.palette.common.blue400,

  // The informational glyph weight, bound to Ripple. Not `blue600`, which is
  // brand and stays on FDS until the brand ticket moves it.
  infoMark: theme.palette.common.infoMark,

  // Purple/indigo — conditional-block chrome. Not a real FDS token (no
  // purple entries in packages/fds-tokens/colors.ts); kept as an explicit
  // custom constant rather than silently minted alongside generated values.
  purple100: '#e0e7ff',
  purple200: '#c7d2fe',
  purple400: '#818cf8',
  purple600: '#4f46e5',

  // Radii — no generated FDS source; local constants.
  // There is deliberately no `radiusXs`. It was 3px, and no design source
  // actually asks for 3px on anything it was applied to: BOTH handoff bundles'
  // stylesheets give the canvas variable chip 4px, and the bundles' own token
  // tables define a --radius-xs: 3px that their components then don't use here
  // (AXHE-4291, CONFORMANCE §2.4). Its four call sites were all the same visual
  // class — small inline mono code chips — and all now use `radiusSm`; at 4px
  // the two tokens would have been one value under two names, so the name is
  // gone rather than aliased.
  radiusSm: 4, // smallest radius on this surface: chips, code spans, rail rows
  radiusMd: 6,
  radiusLg: 8,
  radiusPill: 999,

  // Shadows — no generated FDS source; local constants
  shadowMd: '0 4px 8px rgba(15,23,42,0.08)',
  shadowPopover: '0 4px 16px rgba(15,23,42,0.12)', // top bar chip popovers (AXHE-3814)

  // Type
  fontMono: "'Source Code Pro', 'SF Mono', Menlo, Consolas, monospace",
});

export type FDSTokens = ReturnType<typeof getFDS>;
